import { AppId } from '@inno-tools/container-bridge'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'

const CLUSTERIX_DOMAIN = 'clusterix.io'

export type ShortcutInfo = {
  isInternal: boolean
  appId: AppId | null
  appPath: string | null
  appIcon: string | null
  /**
   * the full path including pathname, search params, and hash
   * example: '/company-search/company/3105492?tab=details#section'
   */
  fullPath: string | null
}

/**
 * analyzes a shortcut URL to determine if it's an internal link
 * and maps it to the corresponding app configuration.
 */
export const getShortcutInfo = (
  shortcutUrl: string,
  apps: ReturnType<typeof useAvailableApps>['apps']
): ShortcutInfo => {
  try {
    const url = new URL(shortcutUrl)
    const hostname = url.hostname.toLowerCase()

    const isInternal =
      hostname === CLUSTERIX_DOMAIN || hostname.endsWith(`.${CLUSTERIX_DOMAIN}`)

    if (!isInternal) {
      return {
        isInternal: false,
        appId: null,
        appPath: null,
        appIcon: null,
        fullPath: null
      }
    }

    const pathname = url.pathname.replace(/^\//, '')
    const fullPath = url.pathname + url.search + url.hash

    const appEntry = Object.entries(apps).find(([, config]) => {
      const configPath = config.path.replace(/^\//, '')
      return pathname.startsWith(configPath)
    })

    if (appEntry) {
      const [appId, config] = appEntry
      return {
        isInternal: true,
        appId: appId as AppId,
        appPath: config.path,
        appIcon: config.appIcon ?? config.icon,
        fullPath
      }
    }

    return {
      isInternal: true,
      appId: null,
      appPath: null,
      appIcon: null,
      fullPath
    }
  } catch {
    return {
      isInternal: false,
      appId: null,
      appPath: null,
      appIcon: null,
      fullPath: null
    }
  }
}
